import { createAsyncThunk } from '@reduxjs/toolkit';
import familyFestApi from '@infrastructure/family-fest-api';
import { mapFamilyFestAttendeeGridRow } from './map-family-fest-attendee-grid-row';
import { fetchAttendeesStart, fetchAttendeesSuccess, fetchAttendeesFailure } from './familyFestAttendeesSlice';
export const fetchFamilyFestAttendees = createAsyncThunk('familyFestAttendees/fetch', async ({ eventId, req }, { dispatch }) => {
    try {
        dispatch(fetchAttendeesStart());
        const response = await familyFestApi.getFamilyFestAttendees(eventId, req);
        const body = response.data;
        const items = (body.items ?? []).map((row) => mapFamilyFestAttendeeGridRow(row));
        dispatch(fetchAttendeesSuccess({ items, totalCount: body.totalCount ?? 0 }));
        return items;
    }
    catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to fetch attendees';
        dispatch(fetchAttendeesFailure(message));
        throw error;
    }
});
export const getFamilyFestCompaniesAsync = createAsyncThunk('familyFestAttendees/getCompanies', async (_, { rejectWithValue }) => {
    try {
        const response = await familyFestApi.getFamilyFestCompanies();
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
const COUNTRY_CODE_TO_ENUM = {
    nl: 1,
    de: 2,
    be: 3,
    se: 4,
    at: 5,
    ch: 6,
};
export const getFamilyFestPickupLocationsAsync = createAsyncThunk('familyFestAttendees/getPickupLocations', async (params, { rejectWithValue }) => {
    try {
        const { addressCountry, companyId } = params ?? {};
        const country = addressCountry ? COUNTRY_CODE_TO_ENUM[addressCountry.toLowerCase()] : undefined;
        const isNl = addressCountry?.toLowerCase() === 'nl';
        const response = await familyFestApi.getFamilyFestPickupLocations(country, isNl ? undefined : companyId);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const getFamilyFestAttendeeDetailAsync = createAsyncThunk('familyFestAttendees/getDetail', async (attendeeId, { rejectWithValue }) => {
    try {
        const response = await familyFestApi.getFamilyFestAttendeeDetail(attendeeId);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const registerFamilyFestAttendeeAdminAsync = createAsyncThunk('familyFestAttendees/registerAdmin', async ({ eventId, body }, { rejectWithValue }) => {
    try {
        const response = await familyFestApi.registerFamilyFestAttendeeAdmin(eventId, body);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const completeFamilyFestRegistrationAsync = createAsyncThunk('familyFestAttendees/completeRegistration', async (body, { rejectWithValue }) => {
    try {
        const response = await familyFestApi.completeFamilyFestRegistration(body);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const archiveFamilyFestAttendeeAsync = createAsyncThunk('familyFestAttendees/archive', async ({ attendeeId, archiveReason }, { rejectWithValue }) => {
    try {
        const response = await familyFestApi.archiveFamilyFestAttendee(attendeeId, { archiveReason });
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const offboardFamilyFestAttendeeAsync = createAsyncThunk('familyFestAttendees/offboard', async (attendeeId, { rejectWithValue }) => {
    try {
        const response = await familyFestApi.offboardFamilyFestAttendee(attendeeId);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const unarchiveFamilyFestAttendeeAsync = createAsyncThunk('familyFestAttendees/unarchive', async (attendeeId, { rejectWithValue }) => {
    try {
        const response = await familyFestApi.unarchiveFamilyFestAttendee(attendeeId);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const deleteFamilyFestAttendeeAsync = createAsyncThunk('familyFestAttendees/delete', async ({ attendeeId, expireInvitation = true }, { rejectWithValue }) => {
    try {
        const response = await familyFestApi.deleteFamilyFestAttendee(attendeeId, expireInvitation);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const generateFamilyFestInviteLinkAsync = createAsyncThunk('familyFestAttendees/generateInviteLink', async (attendeeId, { rejectWithValue }) => {
    try {
        const response = await familyFestApi.generateFamilyFestInviteLink(attendeeId);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const downloadFamilyFestAttendeesExcelAsync = createAsyncThunk('familyFestAttendees/downloadExcel', async (eventId, { rejectWithValue }) => {
    try {
        await familyFestApi.downloadFamilyFestAttendeesExcel(eventId);
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const resendFamilyFestInvitationEmailAsync = createAsyncThunk('familyFestAttendees/resendInvitationEmail', async (attendeeId, { rejectWithValue }) => {
    try {
        const response = await familyFestApi.resendFamilyFestInvitationEmail(attendeeId);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const sendFamilyFestReminderEmailAsync = createAsyncThunk('familyFestAttendees/sendReminderEmail', async (attendeeIds, { rejectWithValue }) => {
    try {
        const response = await familyFestApi.sendFamilyFestReminderEmail(attendeeIds);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const getFamilyFestReminderEligibleAttendeesAsync = createAsyncThunk('familyFestAttendees/getReminderEligible', async ({ eventId, pageNumber, pageSize }, { rejectWithValue }) => {
    try {
        const response = await familyFestApi.getFamilyFestReminderEligibleAttendees(eventId, pageNumber, pageSize);
        const body = response.data;
        const items = (body.items ?? []).map((row) => mapFamilyFestAttendeeGridRow(row));
        return { items, totalCount: body.totalCount ?? 0 };
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const getFamilyFestAttendeesCountAsync = createAsyncThunk('familyFestAttendees/getCount', async ({ eventId, req }, { rejectWithValue }) => {
    try {
        const response = await familyFestApi.getFamilyFestAttendees(eventId, req);
        const body = response.data;
        return body.totalCount ?? 0;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
